import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeAccessorColumnBuilder } from '../../data-table/helpers';
import { TableCellArticle } from '../../../visual-components/table/table-cells/table-cell-article/table-cell-article';
import { Typography } from '../../../visual-components/typography/typography';
import { DataTable } from '../../data-table/data-table';
import { createColumns } from '../../data-table/create-columns';
import { TableLayout } from '../../../visual-components/table-layout/table-layout';
import { Table } from '../../../visual-components/table/table/table';
import { ArticleSearch, Cart, CartItem, Currency, Container } from '../../backend/openapi/api-types';
import { TableCellUnit } from '../../../visual-components/table/table-cells/table-cell-unit/table-cell-unit';
import { TableCellContainer } from '../../../visual-components/table/table-cells/table-cell-container/table-cell-container';
import { getCurrencySymbol } from '../../../domain/article/services/currency';
import { WEIGHT_UNIT } from '../../../domain/article/services/weight';
import { useFormatter } from '../../../core/formatter/use-formatter';
import { GridItem } from '../../../visual-components/grid/grid';
import { QueryErrorHandler } from '../../../visual-components/query-error-handler/query-error-handler';
import { useMaxViewport } from '../../../core/hooks/use-max-viewport';
import { TotalPrice } from './total-price';
import { TableCellWeightDialog } from './table-cell-weight-dialog';
import { TotalAmount } from './total-amount';

export type DeleteItemCellProps = {
  articleId: string;
  container: Container;
  erpContainerArticleId: string;
  weightKg: number;
};

type Props = {
  data: Cart | undefined;
  readOnly?: boolean;
  isLoading?: boolean;
  renderDeleteItemCell: (props: DeleteItemCellProps) => React.ReactNode;
  error?: React.ComponentProps<typeof QueryErrorHandler>['error'];
  debugInfo?: React.ComponentProps<typeof QueryErrorHandler>['debugInfo'];
};

// TODO: finalize styling of table
// TODO: the table cells are not implemented nicely.
export const CartTable: React.FC<Props> = ({
  data,
  readOnly = false,
  isLoading,
  renderDeleteItemCell,
  error,
  debugInfo,
}) => {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatter();
  const isMobile = useMaxViewport('s');

  const items = data?.items;

  const createAccessorColumn = makeAccessorColumnBuilder<CartItem>();

  const colDef = [
    createAccessorColumn('article', {
      headerLabel: t('global.article'),
      cssWidth: '290px',
      renderCell: ({ rowId: pimArticleId, value: { series, hexCode, hasRendering } }) => (
        <TableCellArticle
          hasRendering={hasRendering}
          hexCode={hexCode ?? undefined}
          pimArticleId={pimArticleId}
          series={series.name}
          seriesNumber={series.number}
        />
      ),
      footerLabel: <TotalPrice amount={data?.grandTotal.amount} />,
    }),
    createAccessorColumn('weightKg', {
      headerLabel: t('global.quantity'),
      cssWidth: 'minmax(205px, 1fr)',
      renderCell: ({ value, row: { original } }) => (
        <TableCellWeightDialog
          // TODO: @Simon improve type from original
          article={original.article as ArticleSearch}
          readOnly={readOnly}
          titleDialog={t('article.orderSection.inCart.editPosition')}
          unit={WEIGHT_UNIT}
          weight={value}
        />
      ),
    }),
    createAccessorColumn('container', {
      headerLabel: t('global.selectedContainer'),
      cssWidth: 'minmax(150px, auto)',
      alignment: 'right',
      renderCell: ({ value, row }) => <TableCellContainer container={value} nItems={row.original.quantity} />,
    }),
    createAccessorColumn('pricePerKg', {
      headerLabel: t('global.pricePerUnit', {
        currency: getCurrencySymbol(data?.currency.code ?? Currency.CHF),
        unit: WEIGHT_UNIT,
      }),
      alignment: 'right',
      renderCell: ({ value }) => <TableCellUnit value={formatCurrency(value / 100)} />,
    }),
    createAccessorColumn('lineTotal', {
      headerLabel: t('global.priceInCurrency', {
        value: getCurrencySymbol(data?.currency.code ?? Currency.CHF),
      }),
      alignment: 'right',
      renderCell: ({ value: { amount } }) => <TableCellUnit value={formatCurrency(amount / 100)} />,
      footerLabel: isMobile ? null : <TotalAmount amount={data?.grandTotal.amount} />,
    }),
    createAccessorColumn('article.id', {
      headerLabel: <></>,
      renderCell: ({
        rowId: articleId,
        row: {
          original: { container, erpContainerArticleId, weightKg },
        },
      }) => {
        if (readOnly) {
          return null;
        }
        return renderDeleteItemCell({ articleId, container, erpContainerArticleId, weightKg });
      },
    }),
  ];

  if (data?.isEmpty) {
    return (
      <GridItem>
        <Typography variant="headline-3">{t('cart.documentTitle')}</Typography>
        <Typography variant="maintext">{t('cart.emptyCartDescription')}</Typography>
      </GridItem>
    );
  }

  return (
    <>
      <DataTable
        data={items ?? []}
        isLoading={isLoading}
        columns={createColumns({
          colDef,
          metaValueConfig: {
            mobileHeaderCount: 1,
          },
          rowIdentifierColumnId: 'article.id',
        })}
      >
        {(props) => (
          <TableLayout
            debugInfo={debugInfo}
            error={error}
            table={<Table {...props} isLoading={isLoading} label="cart" />}
            title={t('cart.documentTitle')}
          />
        )}
      </DataTable>
    </>
  );
};
